import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Check, ChevronDown, Star, X, Droplets, Wind, Gift, ArrowRight, ArrowUpRight, Plus, Minus, Volume2, VolumeX } from "lucide-react";
import { Checkbox } from "./components/ui/checkbox";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import AdminPage from "./AdminPage";

const API = process.env.REACT_APP_BACKEND_URL;

// Branches
const BRANCHES = [
  { id: 'cestlice', label: 'Aquapalace', city: 'Čestlice' },
  { id: 'butovice', label: 'Coral Office Park', city: 'Nové Butovice' },
];

const VEHICLE_TYPES = [
  { id: 'sedan', label: 'Sedan / Liftback' },
  { id: 'suv', label: 'SUV / Crossover' },
  { id: 'combi', label: 'Kombi' },
  { id: 'coupe', label: 'Coupé / GT' },
  { id: 'supercar', label: 'Supersport / Hypercar' },
  { id: 'other', label: 'Jiný typ' },
];

const PACKAGE = {
  id: 'detailing',
  title: 'Kompletní detailing',
  price: 2999,
  original: 6500,
  duration: 'Jednorázová péče · 1 den ve studiu',
};

// Detailed breakdown — what's included in the package
const INCLUDED = [
  {
    num: '01',
    title: 'Exteriér',
    subtitle: 'Ruční mytí & dekontaminace',
    items: [
      'Pre-wash aktivní pěnou pro rozpuštění hrubých nečistot',
      'Mytí 2-bucket metodou pH-neutrálním šamponem Koch-Chemie',
      'Odstranění poletavé rzi, smůly a hmyzu z laku',
      'Ruční osušení mikroutěrkami 500 gsm',
    ],
  },
  {
    num: '02',
    title: 'Lak',
    subtitle: 'Jednokrokové rozleštění',
    items: [
      'Jednokrokové ruční leštění Rupes BigFoot',
      'Odstranění povrchových mikroškrábanců',
      'Obnovení hloubky a lesku laku',
      'Foto-dokumentace před / po',
    ],
  },
  {
    num: '03',
    title: 'Interiér',
    subtitle: 'Suché čištění & detail',
    items: [
      'Kompletní vysátí kabiny — koberce, sedadla, škvíry a kolejnice',
      'Vysátí zavazadlového prostoru včetně místa pod kobercem',
      'Ošetření plastů a přístrojové desky (matný finiš)',
      'Kompletní čištění skel zevnitř i zvenku — bez šmouh',
    ],
  },
  {
    num: '04',
    title: 'Detail',
    subtitle: 'Finální kontrola',
    items: [
      'Kontrola pod LED lampou v zatemněném boxu',
      'Ošetření pneumatik',
    ],
  },
];

const PROCESS_STEPS = [
  { num: '01', title: 'Inspekce', desc: 'Foto-dokumentace stavu vozu, měření síly laku pod LED světlem v zatemněném boxu.' },
  { num: '02', title: 'Dekontaminace', desc: '2 bucket metoda mytí pH-neutrálním šamponem, pre-wash, odstranění poletavé rzi, smůly a hmyzu.' },
  { num: '03', title: 'Rozleštění', desc: 'Jednokrokové ruční leštění Rupes BigFoot — obnovení hloubky laku a odstranění povrchových vad.' },
  { num: '04', title: 'Interiér', desc: 'Kompletní suché čištění — vysátí interiéru i kufru, ošetření plastů, leštění skel zevnitř.' },
  { num: '05', title: 'Kontrola', desc: 'Finální kontrola pod LED lampou, doleštění detailů a předání auta majiteli.' },
];

const REVIEWS = [
  { text: 'Moc doporučuji! Přístup naprosto profesionální, komunikace na jedničku a výsledek předčil moje očekávání. Auto vypadá jako nové.', name: 'Petr N.', car: 'Ověřený zákazník · Google' },
  { text: 'Nádherný progres — před a po je rozdíl na první pohled. Kluci si dali skutečně záležet, každý detail sedí. Určitě se vrátím.', name: 'Jana M.', car: 'Ověřený zákazník · Google' },
  { text: 'Skvělý přístup od první zprávy až po předání auta. Cítil jsem, že jim na výsledku opravdu záleží. Perfektní řemeslo.', name: 'Michal V.', car: 'Ověřený zákazník · Google' },
  { text: 'Dlouho jsem hledal detailing, kterému bych svěřil auto bez obav. Konečně mám jistotu — precizní práce a férové jednání. Doporučuji všem.', name: 'Tereza K.', car: 'Ověřený zákazník · Google' },
];;

const FAQS = [
  { q: 'Co všechno je v ceně 2 999 Kč?', a: 'Kompletní ruční mytí exteriéru včetně skel, dekontaminace laku (poletavá rez, smůla, hmyz), jednokrokové rozleštění Rupes BigFoot a suché čištění interiéru — vysátí kabiny i kufru, ošetření plastů, čištění skel zevnitř.' },
  { q: 'Kolik zaplatím za SUV nebo dodávku?', a: 'Základní cena 2 999 Kč platí pro sedan, hatchback a kombi. SUV a crossovery mají příplatek 20 % (3 599 Kč), dodávky, MPV a sedmimístné vozy 30 % (3 899 Kč).' },
  { q: 'Jak dlouho detailing zabere?', a: 'Zpravidla jeden pracovní den. Auto vezmete ráno a vyzvednete si ho odpoledne až podvečer. Přesný čas potvrdíme při rezervaci.' },
  { q: 'Proč je akční cena tak výhodná?', a: 'Chceme, aby si prémiový detailing mohl dovolit i nový zákazník. Při první návštěvě dáváme 54 % slevu, abyste rozdíl viděli na vlastní oči.' },
  { q: 'Můžu si přidat další služby (keramika, tepování)?', a: 'Ano. Po detailingu vám rádi doporučíme, které kroky se vyplatí — od keramické ochrany po hloubkové tepování. Nabídku sestavíme na míru.' },
];

const bonusOptions = [
  { id: 'nano-water', title: 'NanoVision — Voda do ostřikovačů', icon: Droplets },
  { id: 'ozone', title: 'Ozonování & čištění klimatizace', icon: Wind },
];

// ═══ Before/After Slider ═══
const BeforeAfterSlider = ({ before, after, label }) => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const updateFromX = (clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, x)));
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      updateFromX(x);
    };
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  const start = (e) => {
    draggingRef.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromX(x);
  };

  return (
    <div ref={containerRef} className="relative w-full h-[55vh] sm:h-[75vh] overflow-hidden ew-cursor select-none bg-black"
      onMouseDown={start} onTouchStart={start} data-testid="before-after-slider">
      <img src={after} alt="Po keramické ochraně" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={before} alt="Před" className="absolute inset-0 h-full object-cover" style={{ width: containerRef.current ? `${containerRef.current.getBoundingClientRect().width}px` : '100%' }} draggable={false} />
      </div>
      <div className="absolute top-0 bottom-0 w-px bg-white" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 bg-white flex items-center justify-center shadow-2xl">
          <div className="flex items-center gap-1">
            <span className="w-1 h-4 bg-black" />
            <span className="w-1 h-5 bg-black" />
            <span className="w-1 h-4 bg-black" />
          </div>
        </div>
      </div>
      <div className="absolute top-5 left-5 pointer-events-none">
        <span className="label text-white bg-black/60 px-3 py-1.5 backdrop-blur-sm">Před</span>
      </div>
      <div className="absolute top-5 right-5 pointer-events-none">
        <span className="label text-black bg-white px-3 py-1.5">Po</span>
      </div>
      {label && (
        <div className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 pointer-events-none">
          <p className="label text-white/80 mb-2">Transformace</p>
          <p className="text-white text-2xl sm:text-4xl font-medium tracking-tight">{label}</p>
        </div>
      )}
    </div>
  );
};

// ═══ Process Video — vertical studio recording with sound toggle ═══
const ProcessVideo = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) { v.play().catch(() => {}); setPlaying(true); }
    setMuted(v.muted);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }} className="mt-20 sm:mt-32 pt-16 sm:pt-24 border-t border-black/10">
      <div className="grid lg:grid-cols-12 gap-8 sm:gap-16 items-start">
        {/* Copy */}
        <div className="lg:col-span-5 lg:sticky lg:top-32">
          <p className="label text-brand mb-6" data-testid="process-video-eyebrow">— Bonus · Skutečná zakázka</p>
          <h3 className="text-3xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.03em] text-black leading-[1.02] mb-8">
            Živý záznam<br /><span className="text-black/35">ze studia.</span>
          </h3>
          <div className="space-y-5 text-black/60 text-base leading-[1.75] max-w-md">
            <p>Autentický záběr z naší dílny — bez střihů, bez efektů. Slyšíte skutečný zvuk leštičky Rupes BigFoot i pocit prostoru studia.</p>
            <p className="text-black">
              <span className="inline-block bg-black text-brand label px-2.5 py-1 mr-2">Extra</span>
              Tento vůz obdržel navíc <span className="font-semibold">keramickou ochranu</span> nad rámec základního detailingu.
            </p>
            <p className="text-black/45 text-sm">Ceramic coating není součástí akce 2 999 Kč — na požádání připravíme individuální cenovou nabídku.</p>
          </div>
        </div>

        {/* Vertical video 9:16 */}
        <div className="lg:col-span-7">
          <div className="relative bg-black overflow-hidden group mx-auto max-w-[420px] lg:max-w-[520px]" style={{ aspectRatio: '9 / 16' }} data-testid="process-video-wrap">
            <video
              ref={videoRef}
              className="w-full h-full object-cover cursor-pointer"
              autoPlay
              muted
              loop
              playsInline
              poster="/gallery/process_ceramic_poster.jpg"
              onClick={togglePlay}
              data-testid="process-video"
            >
              <source src="/gallery/process_ceramic.mp4" type="video/mp4" />
            </video>

            {/* Top labels */}
            <div className="absolute top-4 left-4 flex items-center gap-2 pointer-events-none">
              <span className="label bg-white/95 text-black px-2.5 py-1">Live · Studio</span>
              <span className="label bg-brand text-black px-2.5 py-1">+ Ceramic</span>
            </div>

            {/* Bottom overlay caption */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-5 sm:p-7 pointer-events-none">
              <p className="label text-brand mb-2">Rupes BigFoot · Ruční práce</p>
              <p className="text-white text-base sm:text-xl font-semibold tracking-tight leading-tight">
                Zvuk, který slyšíte, je opravdový.
              </p>
            </div>

            {/* Sound toggle */}
            <button
              type="button"
              onClick={toggleSound}
              aria-label={muted ? 'Zapnout zvuk' : 'Vypnout zvuk'}
              data-testid="process-video-sound"
              className="absolute top-4 right-4 w-11 h-11 bg-white/95 hover:bg-brand text-black flex items-center justify-center transition-colors"
            >
              {muted ? <VolumeX className="w-4.5 h-4.5" size={18} /> : <Volume2 className="w-4.5 h-4.5" size={18} />}
            </button>

            {/* Play state pill */}
            {!playing && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-16 h-16 bg-white/90 flex items-center justify-center">
                  <span className="label text-black">Pauza</span>
                </div>
              </div>
            )}
          </div>

          <p className="text-center text-black/40 label mt-5">Klikni na video pro pause · na ikonu pro zvuk</p>
        </div>
      </div>
    </motion.div>
  );
};


// ═══ Spots Counter ═══
const SpotsCounter = () => {
  const [spots, setSpots] = useState(30);
  useEffect(() => {
    const fetchSpots = async () => {
      try { const r = await fetch(`${API}/api/spots`); const d = await r.json(); setSpots(d.spots); } catch (e) {}
    };
    fetchSpots();
    const interval = setInterval(fetchSpots, 30000);
    return () => clearInterval(interval);
  }, []);
  return (
    <span className="label flex items-center gap-2.5 text-black/60" data-testid="spots-counter">
      <span className="w-1.5 h-1.5 bg-black rounded-full pulse-dot" />
      <span>Akčních míst</span>
      <span className="text-black font-semibold tabular-numbers" data-testid="spots-number">{String(spots).padStart(2, '0')}</span>
    </span>
  );
};

// ═══ Bonus Popup ═══
const BonusPopup = ({ show, onClose, onBonusSelect, selectedBonus }) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const isExpired = timeLeft <= 0;
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  useEffect(() => {
    if (!show) return;
    setTimeLeft(60);
    const interval = setInterval(() => setTimeLeft((prev) => (prev <= 0 ? 0 : prev - 1)), 1000);
    return () => clearInterval(interval);
  }, [show]);

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md">
        <motion.div initial={{ scale: 0.96, opacity: 0, y: 16 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="bg-white max-w-md w-full p-9 sm:p-11 relative shadow-2xl">
          <button onClick={onClose} className="absolute top-5 right-5 text-black/40 hover:text-black transition-colors" data-testid="bonus-close">
            <X className="w-4 h-4" />
          </button>
          <div className="mb-8">
            <p className="label text-black/55 mb-4">— Bonus zdarma</p>
            <h3 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-black mb-3">Volba je vaše.</h3>
            <p className="text-black/60 text-sm">V hodnotě <span className="text-black font-medium">900 Kč</span> ke každé objednávce.</p>
          </div>
          <div className="space-y-2 mb-7">
            {bonusOptions.map((bonus) => {
              const Icon = bonus.icon;
              const selected = selectedBonus === bonus.id;
              return (
                <button key={bonus.id} onClick={() => onBonusSelect(bonus.id)} disabled={isExpired}
                  className={`w-full p-4 flex items-center gap-3 transition-all border ${
                    selected ? 'bg-black text-white border-black' : 'bg-white border-black/15 text-black/80 hover:border-black/40'
                  } ${isExpired ? 'opacity-30 cursor-not-allowed' : ''}`}>
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 text-left text-sm">{bonus.title}</span>
                  {selected && <Check className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
          <div className="flex items-baseline justify-between mb-6 pb-5 border-b border-black/10">
            <p className="label text-black/55">{isExpired ? 'Vypršelo' : 'Vyprší za'}</p>
            <p className="text-2xl font-semibold tracking-tight text-black tabular-numbers">{String(minutes).padStart(2, '0')}<span className="text-black/30">:</span>{String(seconds).padStart(2, '0')}</p>
          </div>
          <button onClick={() => { if (selectedBonus && !isExpired) onClose(); }} disabled={isExpired || !selectedBonus}
            data-testid="bonus-activate"
            className={`w-full py-4 label transition-all ${
              isExpired || !selectedBonus ? 'bg-black/5 text-black/25 cursor-not-allowed' : 'bg-black text-white hover:bg-black/85'
            }`}>
            {isExpired ? 'Nabídka vypršela' : !selectedBonus ? 'Vyberte bonus' : 'Aktivovat bonus'}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ═══ Section Eyebrow ═══
const SectionEyebrow = ({ num, label }) => (
  <div className="flex items-center gap-4 mb-10 sm:mb-14">
    <span className="label text-brand">— {num}</span>
    <span className="label text-black">{label}</span>
  </div>
);

// ═══ FAQ Item ═══
const FAQItem = ({ q, a, isOpen, onToggle }) => (
  <div className="border-b border-black/10">
    <button onClick={onToggle} className="w-full py-7 sm:py-9 flex items-center justify-between gap-6 text-left group">
      <span className="text-lg sm:text-3xl font-medium tracking-tight text-black pr-4">{q}</span>
      <span className="flex-shrink-0 w-10 h-10 border border-black/15 flex items-center justify-center group-hover:bg-black group-hover:text-white group-hover:border-black transition-all">
        {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
      </span>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden">
          <p className="pb-8 text-black/60 text-base sm:text-lg leading-relaxed max-w-2xl">{a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

function LandingPage() {
  const [showBonusPopup, setShowBonusPopup] = useState(false);
  const [bonusAccepted, setBonusAccepted] = useState(false);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [selectedService] = useState('detailing');
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', vehicle_type: '', branch: '', bonus_type: '',
    selected_upgrades: [], gdpr_consent: false, campaign: 'mono-2026',
  });

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  useEffect(() => {
    const timer = setTimeout(() => setShowBonusPopup(true), 9000);
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll);
    return () => { clearTimeout(timer); window.removeEventListener('scroll', onScroll); };
  }, []);

  const handleInputChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleVehicleSelect = (id) => {
    setFormData((prev) => ({ ...prev, vehicle_type: id }));
    setShowVehicleDropdown(false);
  };

  const handleBonusSelect = (bonusId) => {
    setFormData((prev) => ({ ...prev, bonus_type: bonusId }));
    setBonusAccepted(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name || !formData.phone || !formData.vehicle_type || !formData.branch) {
      setError('Vyplňte prosím všechna povinná pole.');
      return;
    }
    if (!formData.gdpr_consent) { setError('Musíte souhlasit se zpracováním osobních údajů.'); return; }
    setIsSubmitting(true);
    try {
      const payload = { ...formData, email: formData.email || 'not-provided@carspa.cz' };
      if (selectedService) payload.selected_upgrades = [selectedService];
      const res = await fetch(`${API}/api/leads`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error();
      setSubmitted(true);
    } catch { setError('Nepodařilo se odeslat. Zkuste to prosím znovu.'); }
    setIsSubmitting(false);
  };

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }
  });

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="text-center max-w-md">
          <div className="w-12 h-12 mx-auto bg-black flex items-center justify-center mb-10">
            <Check className="w-5 h-5 text-white" />
          </div>
          <p className="label text-black/55 mb-6">— Potvrzeno</p>
          <h2 className="text-6xl sm:text-7xl font-semibold tracking-tighter text-black mb-7">Děkujeme.</h2>
          <p className="text-black/60 text-base sm:text-lg leading-relaxed mb-10">Náš seniorní detailer se vám ozve do 24 hodin s nabídkou termínů a osobním plánem péče.</p>
          <div className="hairline mb-8" />
          <p className="label text-black/55 mb-3">Preferujete osobní kontakt?</p>
          <a href="tel:+420730595595" className="text-black text-2xl font-medium hover:opacity-60 transition-opacity">+420 730 595 595</a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black">
      <BonusPopup show={showBonusPopup} onClose={() => setShowBonusPopup(false)} onBonusSelect={handleBonusSelect} selectedBonus={formData.bonus_type} />

      {/* ═══ Nav ═══ */}
      <motion.header initial={{ y: -16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-black border-b border-white/10' : 'bg-black/95 backdrop-blur-md'}`}>
        <div className="max-w-[1600px] mx-auto px-5 sm:px-10 lg:px-16 h-16 sm:h-20 flex items-center justify-between">
          <a href="#" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="text-xl sm:text-2xl font-bold tracking-tighter text-white">CARSPA<span className="text-brand">.</span></span>
          </a>
          <nav className="hidden md:flex items-center gap-10 label">
            <a href="#why" className="text-white/60 hover:text-brand transition-colors">Proč</a>
            <a href="#transform" className="text-white/60 hover:text-brand transition-colors">Transformace</a>
            <a href="#process" className="text-white/60 hover:text-brand transition-colors">Proces</a>
            <a href="#pricing" className="text-white/60 hover:text-brand transition-colors">Ceník</a>
            <a href="#faq" className="text-white/60 hover:text-brand transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-3 sm:gap-6">
            <div className="hidden lg:flex items-center gap-2 label text-white/55">
              <span className="w-1.5 h-1.5 bg-brand rounded-full pulse-dot" />
              <span>Akčních míst</span>
              <NavSpots />
            </div>
            <a href="#form" data-testid="cta-nav" className="inline-flex items-center gap-2 bg-brand text-black px-4 sm:px-5 py-2 sm:py-2.5 label hover:bg-brand-dark transition-all text-[10px] sm:text-[11px]">
              <span className="hidden sm:inline">Rezervovat</span>
              <span className="sm:hidden">Rezervace</span>
              <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
        </div>
        {bonusAccepted && !showBonusPopup && (
          <div className="border-t border-white/10 py-2 px-4 bg-brand text-black">
            <div className="flex items-center justify-center gap-2 label">
              <Check className="w-3 h-3" />
              <span>Bonus aktivován · {formData.bonus_type === 'nano-water' ? 'NanoVision' : 'Ozonování'} zdarma</span>
            </div>
          </div>
        )}
      </motion.header>

      {/* ═══ 1. HERO — WHAT · WHY · PRICE in 3 seconds ═══ */}
      <section ref={heroRef} className="relative min-h-screen flex items-center py-10 sm:py-20 pt-16 sm:pt-24 bg-black text-white" data-testid="hero-section">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-10 lg:px-16 w-full">
          {/* CARSPA wordmark — giant brand statement */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-5 sm:mb-14 flex items-center gap-3 sm:gap-4">
            <span className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.04em] text-white">CARSPA<span className="text-brand">.</span></span>
            <span className="hidden sm:inline-block w-12 h-px bg-white/20" />
            <span className="hidden sm:inline label text-white/55">Profesionální ruční mytí · Detailing</span>
          </motion.div>

          <div className="grid grid-cols-12 gap-5 sm:gap-10 items-center">
            {/* LEFT — WHAT/WHY/PRICE/CTA */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 lg:col-span-7">

              {/* WHAT — Service tags */}
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-5 sm:mb-7">
                {['Ruční mytí', 'Dekontaminace', 'Rozleštění laku', 'Suché čištění interiéru'].map((tag, i) => (
                  <span key={i} className="label bg-brand text-black px-2.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-[11px]">{tag}</span>
                ))}
              </div>

              {/* HEADLINE — single strong message */}
              <h1 className="text-[34px] leading-[0.96] sm:text-[80px] lg:text-[104px] lg:leading-[0.88] font-semibold tracking-[-0.045em] text-white mb-6 sm:mb-8">
                Za půlku ceny.<br />
                <span className="text-brand">S plnou pozorností.</span>
              </h1>

              {/* WHY — 3 reasons */}
              <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-7 sm:mb-10 max-w-2xl">
                {[
                  { stat: '12+', label: 'let zkušeností' },
                  { stat: '100%', label: 'ruční práce' },
                  { stat: '4.9', label: 'Google rating' },
                ].map((item, i) => (
                  <div key={i} className="border-l-2 border-brand pl-2.5 sm:pl-4">
                    <p className="text-xl sm:text-3xl font-semibold tracking-tight text-white tabular-numbers">{item.stat}</p>
                    <p className="label text-white/55 mt-1 leading-tight text-[9px] sm:text-[11px]">{item.label}</p>
                  </div>
                ))}
              </div>

              {/* PRICE + CTA */}
              <div className="flex flex-wrap items-end gap-x-6 sm:gap-x-8 gap-y-4 sm:gap-y-5 mb-6 sm:mb-7">
                <div>
                  <p className="label text-white/55 mb-1 text-[10px] sm:text-[11px]">Kompletní detailing · dnes za</p>
                  <div className="flex items-baseline gap-3">
                    <p className="text-4xl sm:text-6xl font-semibold tracking-tighter text-white tabular-numbers leading-none">
                      2 999 <span className="text-white/45 text-2xl sm:text-3xl">Kč</span>
                    </p>
                    <p className="text-xl sm:text-2xl text-white/40 line-through tabular-numbers leading-none">6 500 Kč</p>
                  </div>
                  <p className="label text-brand mt-2 text-[10px] sm:text-[11px]">Ušetříte 3 501 Kč · 54 % sleva</p>
                </div>
                <a href="#form" data-testid="cta-button"
                  className="group inline-flex items-center gap-2 sm:gap-3 bg-brand text-black px-6 sm:px-8 py-3.5 sm:py-4 label hover:bg-brand-dark transition-all w-full sm:w-auto justify-center text-[10px] sm:text-[11px]">
                  Rezervovat termín
                  <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform group-hover:translate-x-1.5" />
                </a>
              </div>

              {/* Trust line */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-5 label text-white/55 text-[9px] sm:text-[11px]">
                <span className="flex items-center gap-1.5">
                  <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-brand" /> Koch-Chemie · Rupes BigFoot · Swiss Wax
                </span>
                <span className="w-px h-3 bg-white/20 hidden sm:block" />
                <span className="flex items-center gap-1.5">
                  <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-brand" /> 2 pobočky Praha
                </span>
              </div>
            </motion.div>

            {/* RIGHT — Quick reservation form */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 lg:col-span-5 lg:pl-4 mt-6 lg:mt-0">
              <form onSubmit={handleSubmit} data-testid="hero-form" className="bg-white text-black p-6 sm:p-8">
                <div className="flex items-baseline justify-between mb-5">
                  <p className="label text-black">Rychlá rezervace</p>
                  <p className="label text-brand">● 24 h odpověď</p>
                </div>
                <h3 className="text-2xl sm:text-3xl font-semibold tracking-tighter text-black mb-1 leading-tight">Zabookujte si termín.</h3>
                <p className="text-black/55 text-sm mb-6">Ozveme se do 24 hodin s volnými termíny.</p>

                <div className="space-y-4">
                  <div>
                    <label className="label text-black/55 mb-2 block">Jméno *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Jan Novák"
                      className="w-full bg-transparent border-b border-black/15 focus:border-black py-2.5 text-black text-base placeholder:text-black/25 focus:outline-none transition-all"
                      data-testid="hero-input-name" />
                  </div>
                  <div>
                    <label className="label text-black/55 mb-2 block">Telefon *</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+420 123 456 789"
                      className="w-full bg-transparent border-b border-black/15 focus:border-black py-2.5 text-black text-base placeholder:text-black/25 focus:outline-none transition-all"
                      data-testid="hero-input-phone" />
                  </div>
                  <div>
                    <label className="label text-black/55 mb-2 block">Pobočka *</label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {BRANCHES.map((branch) => {
                        const { id, city } = branch;
                        const selected = formData.branch === id;
                        return (
                          <button key={id} type="button" onClick={() => setFormData((prev) => ({ ...prev, branch: id }))}
                            className={`p-3 text-left transition-all border ${selected ? 'bg-black text-white border-black' : 'bg-white text-black border-black/15 hover:border-black/40'}`}
                            data-testid={`hero-branch-${id}`}>
                            <span className="text-sm font-medium block">{city}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="label text-black/55 mb-2 block">Typ vozidla *</label>
                    <div className="relative">
                      <button type="button" onClick={() => setShowVehicleDropdown(!showVehicleDropdown)}
                        className="w-full bg-transparent border-b border-black/15 focus:border-black py-2.5 text-left flex items-center justify-between focus:outline-none transition-all"
                        data-testid="hero-vehicle-dropdown">
                        <span className={formData.vehicle_type ? "text-black text-base" : "text-black/30 text-base"}>
                          {formData.vehicle_type ? VEHICLE_TYPES.find((v) => v.id === formData.vehicle_type)?.label : "Vyberte typ vozidla"}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-black/50 transition-transform ${showVehicleDropdown ? "rotate-180" : ""}`} />
                      </button>
                      <AnimatePresence>
                        {showVehicleDropdown && (
                          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                            className="absolute top-full left-0 right-0 mt-1 bg-white border border-black/15 z-20 shadow-xl">
                            {VEHICLE_TYPES.map((v) => (
                              <button key={v.id} type="button" onClick={() => handleVehicleSelect(v.id)}
                                className={`w-full px-4 py-3 text-left text-sm hover:bg-black hover:text-white transition-colors ${formData.vehicle_type === v.id ? "text-black bg-black/[0.04]" : "text-black/75"}`}
                                data-testid={`hero-vehicle-option-${v.id}`}>{v.label}</button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 pt-1">
                    <Checkbox checked={formData.gdpr_consent} onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, gdpr_consent: checked }))}
                      className="mt-0.5 border-black/30 data-[state=checked]:bg-black data-[state=checked]:text-white data-[state=checked]:border-black" data-testid="hero-gdpr-checkbox" />
                    <label className="text-black/55 text-[11px] leading-relaxed cursor-pointer" onClick={() => setFormData((prev) => ({ ...prev, gdpr_consent: !prev.gdpr_consent }))}>
                      Souhlasím se zpracováním osobních údajů dle GDPR.
                    </label>
                  </div>

                  {error && (
                    <div className="p-3 border border-red-500/40 bg-red-50 text-red-600 text-xs" data-testid="hero-error">{error}</div>
                  )}

                  <button type="submit" disabled={isSubmitting} data-testid="hero-submit"
                    className="w-full bg-brand hover:bg-brand-dark disabled:bg-black/15 text-black disabled:text-black/40 py-4 label transition-all disabled:cursor-not-allowed group">
                    {isSubmitting ? "Odesílám..." : (
                      <span className="inline-flex items-center gap-3">
                        Rezervovat za 2 999 Kč
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1.5" />
                      </span>
                    )}
                  </button>

                  <p className="text-center label text-black/40">nebo volejte <a href="tel:+420730595595" className="text-black hover:opacity-70 transition-opacity" data-testid="hero-phone">+420 730 595 595</a></p>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ MARQUEE (Brands) ═══ */}
      <section className="border-y border-black/10 py-7 overflow-hidden bg-white">
        <div className="flex marquee whitespace-nowrap">
          {[...Array(3)].map((_, copyIdx) => (
            <div key={copyIdx} className="flex items-center gap-12 sm:gap-20 pr-12 sm:pr-20">
              {['Koch-Chemie', '·', 'Rupes BigFoot', '·', 'Swiss Wax', '·', 'Keramika Koch', '·', 'Made in Czechia', '·'].map((word, i) => (
                <span key={i} className="text-2xl sm:text-4xl font-medium tracking-tight text-black/85">{word}</span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ═══ 2. WHY — Editorial Problem ═══ */}
      <section id="why" className="py-20 sm:py-44 px-5 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-[1600px] mx-auto">
          <SectionEyebrow num="01" label="Proč to potřebujete" />
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <motion.div {...fadeUp(0)} className="lg:col-span-7">
              <h2 className="text-4xl sm:text-7xl lg:text-[112px] lg:leading-[0.92] font-semibold tracking-[-0.04em] text-black">
                Každý kilometr<br />okrádá váš lak<br />o <span className="text-black/35">hloubku.</span>
              </h2>
            </motion.div>
            <motion.div {...fadeUp(0.1)} className="lg:col-span-5 lg:pt-32">
              <p className="text-black/65 text-base sm:text-lg leading-[1.75] mb-8">
                Automatické myčky, UV záření, ptačí trus, dopravní film a špatná péče vytváří síť mikroškrábanců a oxidaci. Po roce ztrácí lak hloubku. Po třech přestává vypadat jako vaše auto.
              </p>
              <div className="grid grid-cols-2 gap-5">
                {['Mikroškrábance', 'Oxidace laku', 'Vodní skvrny', 'Smůla & hmyz', 'Ztráta lesku', 'Snížená hodnota'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-1 h-1 bg-black" />
                    <span className="text-black/75 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 3. TRANSFORMATION ═══ */}
      <section id="transform" className="py-16 sm:py-36 bg-white">
        <div className="max-w-[1600px] mx-auto px-5 sm:px-10 lg:px-16 mb-12 sm:mb-20">
          <SectionEyebrow num="02" label="Transformace" />
          <motion.h2 {...fadeUp()} className="text-4xl sm:text-7xl lg:text-[112px] lg:leading-[0.92] font-semibold tracking-[-0.04em] text-black max-w-[1100px]">
            Před. <span className="text-black/35">A po.</span>
          </motion.h2>
          <motion.p {...fadeUp(0.1)} className="label text-black/45 mt-7">Skutečné zakázky · Foto-dokumentace ze studia</motion.p>
        </div>

        {/* Featured 50/50 shots — each photo already shows before/after side-by-side */}
        <motion.div {...fadeUp(0.2)} className="px-5 sm:px-10 lg:px-16">
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { src: '/gallery/red_before.jpeg', label: 'Červený lak · 50/50 rozleštění' },
              { src: '/gallery/red_after.jpeg', label: 'Detail povrchu · rozdíl na jednom panelu' },
            ].map((img, i) => (
              <div key={i} className="relative bg-black overflow-hidden group h-[38vh] sm:h-[70vh]" data-testid={`transform-${i}`}>
                <img src={img.src} alt={img.label} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute top-5 left-5 flex items-center gap-2">
                  <span className="label bg-white/95 text-black px-2.5 py-1">Před / Po</span>
                </div>
                <div className="absolute bottom-6 left-6">
                  <p className="label text-brand mb-1.5">Skutečná zakázka</p>
                  <p className="text-white text-lg sm:text-2xl font-semibold tracking-tight">{img.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Full-width 50/50 hero shot — black paint correction */}
          <motion.div {...fadeUp(0.15)} className="relative bg-black overflow-hidden group h-[42vh] sm:h-[80vh] mt-2" data-testid="transform-2">
            <img
              src="/gallery/transformation2.webp"
              alt="Černý lak — 50/50 srovnání před a po jednokrokovém rozleštění"
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute top-5 left-5 flex items-center gap-2">
              <span className="label bg-white/95 text-black px-2.5 py-1">Před / Po</span>
              <span className="label bg-brand text-black px-2.5 py-1">50 / 50</span>
            </div>
            <div className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 max-w-[720px]">
              <p className="label text-brand mb-1.5">Skutečná zakázka · Černý lak</p>
              <p className="text-white text-xl sm:text-3xl font-semibold tracking-tight leading-[1.1]">
                Matný, poškrábaný povrch. <span className="text-white/50">Zrcadlový lesk na jeden zátah.</span>
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Cinematic gallery */}
        <div className="max-w-[1600px] mx-auto px-5 sm:px-10 lg:px-16 mt-2">
          <div className="grid sm:grid-cols-3 gap-2">
            {[
              { src: '/gallery/porsche.jpeg', alt: 'Porsche po detailingu ve studiu CARSPA' },
              { src: '/gallery/correction1.jpeg', alt: 'Detail rozleštění Rupes BigFoot — 50/50 ukázka před a po' },
              { src: '/gallery/black_correction.jpeg', alt: 'Černý lak po jednokrokovém rozleštění Rupes BigFoot' },
            ].map((img, i) => (
              <motion.div key={i} {...fadeUp(i * 0.08)} className="relative overflow-hidden bg-black aspect-[4/5] group">
                <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <p className="label text-white/80">0{i + 1} · Studio</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 4. FEATURED VIDEO — Full Black Block ═══ */}
      <section className="bg-black py-16 sm:py-36 px-5 sm:px-10 lg:px-16">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-7">
              <p className="label text-white/55 mb-6">— 03 · Studio</p>
              <h2 className="text-4xl sm:text-7xl lg:text-[112px] lg:leading-[0.92] font-semibold tracking-[-0.04em] text-white">
                Obsesivní.<br />Posedlá <span className="text-white/35">detailem.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:pt-32">
              <p className="text-white/70 text-base sm:text-lg leading-[1.75]">
                Naši detaileři přistupují ke každému autu jako k jedinečnému kusu. Žádné automatizace, žádné kompromisy. Jen ruční práce ve studiovém prostředí.
              </p>
            </div>
          </div>

          <motion.div {...fadeUp()} className="relative overflow-hidden bg-black h-[45vh] sm:h-[80vh] group">
            <video className="w-full h-full object-cover" autoPlay muted loop playsInline poster="/gallery/work_poster.jpeg" data-testid="gallery-work-video">
              <source src="/gallery/work.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 pointer-events-none">
              <p className="label text-white/80 mb-3">Studio Cestlice</p>
              <p className="text-white text-2xl sm:text-4xl font-medium tracking-tight">Ruční práce, kterou není vidět spěchat.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 5. PROCESS ═══ */}
      <section id="process" className="py-20 sm:py-44 px-5 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-[1600px] mx-auto">
          <SectionEyebrow num="04" label="Studiový proces" />
          <motion.h2 {...fadeUp()} className="text-4xl sm:text-7xl lg:text-[112px] lg:leading-[0.92] font-semibold tracking-[-0.04em] text-black max-w-[1100px] mb-16 sm:mb-24">
            Pět kroků.<br /><span className="text-black/35">Jeden standard.</span>
          </motion.h2>

          <div className="border-t border-black/10">
            {PROCESS_STEPS.map((step, i) => {
              const { num, title, desc } = step;
              return (
                <motion.div key={num} {...fadeUp(i * 0.05)} className="border-b border-black/10 py-6 sm:py-12 grid grid-cols-12 gap-6 sm:gap-12 group hover:bg-black hover:text-white transition-colors duration-500">
                  <div className="col-span-2 sm:col-span-1 px-1 sm:px-4">
                    <span className="label text-black/40 group-hover:text-white/50 transition-colors">{num}</span>
                  </div>
                  <div className="col-span-10 sm:col-span-4 px-1">
                    <h3 className="text-2xl sm:text-5xl font-medium tracking-tight transition-transform duration-500 group-hover:translate-x-2">{title}</h3>
                  </div>
                  <div className="col-span-12 sm:col-span-7 px-1">
                    <p className="text-black/60 group-hover:text-white/70 text-base leading-[1.75] transition-colors max-w-xl">{desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* ═══ Live studio recording — real project with ceramic protection ═══ */}
          <ProcessVideo />
        </div>
      </section>

      {/* ═══ 6. REVIEWS ═══ */}
      <section className="py-20 sm:py-44 px-5 sm:px-10 lg:px-16 bg-[#fafafa]">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-8 mb-16 sm:mb-20">
            <div>
              <SectionEyebrow num="05" label="Reference" />
              <motion.h2 {...fadeUp()} className="text-4xl sm:text-7xl lg:text-[112px] lg:leading-[0.92] font-semibold tracking-[-0.04em] text-black">
                Slovo těch,<br />kteří <span className="text-black/35">vědí.</span>
              </motion.h2>
            </div>
            <motion.div {...fadeUp(0.1)} className="flex items-center gap-3 pb-4">
              <div className="flex gap-1">
                {[0,1,2,3,4].map((s) => <Star key={s} className="w-3.5 h-3.5 text-black fill-black" />)}
              </div>
              <span className="label text-black/55">4.9 · Google · 200+ recenzí</span>
            </motion.div>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            {REVIEWS.map((review, i) => {
              const { text, name, car } = review;
              return (
                <motion.figure key={i} {...fadeUp(i * 0.08)} className="bg-white p-6 sm:p-12 border border-black/8 hover:border-black transition-colors duration-500">
                  <p className="text-xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-black leading-[1.25] mb-10">"{text}"</p>
                  <figcaption className="flex items-center gap-4 pt-7 border-t border-black/10">
                    <div className="w-10 h-10 bg-black text-white flex items-center justify-center label">
                      {name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="text-black text-sm font-medium">{name}</p>
                      <p className="label text-black/45 mt-0.5">{car}</p>
                    </div>
                  </figcaption>
                </motion.figure>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ 7. PRICE LOCK EXCLUSIVITY ═══ */}
      <section className="py-20 sm:py-52 px-5 sm:px-10 lg:px-16 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp()}>
            <p className="label text-brand mb-8">— 06 · Akční nabídka</p>
            <h2 className="text-4xl sm:text-7xl lg:text-[120px] lg:leading-[0.92] font-semibold tracking-[-0.04em] text-white mb-10">
              Detailing za <span className="text-brand">půlku.</span><br />Jen teď.
            </h2>
            <p className="text-white/65 text-base sm:text-lg leading-[1.75] max-w-xl mx-auto mb-10">
              Akční cena <span className="text-white font-semibold">2 999 Kč</span> místo běžných 6 500 Kč platí pouze do <span className="text-white font-semibold">31. 12. 2026</span>. Zabookujte si termín ještě dnes — kapacita studia je omezená.
            </p>

            <div className="grid sm:grid-cols-3 gap-px bg-white/10 mb-12 border border-white/10">
              <div className="bg-black p-5 sm:p-7">
                <p className="label text-white/45 mb-3">Běžná cena</p>
                <p className="text-3xl sm:text-4xl font-semibold tracking-tight text-white/55 tabular-numbers line-through">6 500 <span className="text-white/40 text-lg no-underline">Kč</span></p>
                <p className="label text-white/45 mt-3">Od 1. 1. 2027</p>
              </div>
              <div className="bg-black p-5 sm:p-7 relative">
                <span className="absolute top-3 right-3 label bg-brand text-black px-2 py-0.5">Nyní</span>
                <p className="label text-brand mb-3">Vaše cena dnes</p>
                <p className="text-3xl sm:text-4xl font-semibold tracking-tight text-white tabular-numbers"><span className="text-brand">2 999</span> <span className="text-white/45 text-lg">Kč</span></p>
                <p className="label text-brand mt-3">Ušetříte 3 501 Kč</p>
              </div>
              <div className="bg-black p-5 sm:p-7">
                <p className="label text-white/45 mb-3">Sleva</p>
                <p className="text-3xl sm:text-4xl font-semibold tracking-tight text-white tabular-numbers">−54 <span className="text-white/45 text-lg">%</span></p>
                <p className="label text-white/45 mt-3">Ne akvizice</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
              <a href="#form" data-testid="exclusivity-cta" className="group inline-flex items-center gap-3 bg-brand text-black px-8 py-4 label hover:bg-brand-dark transition-all">
                Rezervovat za 2 999 Kč
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1.5" />
              </a>
              <span className="label text-white/55">
                <span className="text-brand">●</span> Platí do 31. 12. 2026
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 8. WHAT'S INCLUDED — Detailed breakdown ═══ */}
      <section id="pricing" className="py-20 sm:py-44 px-5 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-[1600px] mx-auto">
          <SectionEyebrow num="07" label="Co je v ceně" />

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 mb-16 sm:mb-20 items-end">
            <motion.div {...fadeUp()} className="lg:col-span-8">
              <h2 className="text-4xl sm:text-7xl lg:text-[112px] lg:leading-[0.92] font-semibold tracking-[-0.04em] text-black">
                Detailing od A do Z.<br /><span className="text-black/35">Za akční cenu.</span>
              </h2>
            </motion.div>
            <motion.div {...fadeUp(0.1)} className="lg:col-span-4">
              <div className="bg-black text-white p-7 sm:p-9">
                <p className="label text-brand mb-3">Dnes za</p>
                <div className="flex items-baseline gap-3 mb-3">
                  <p className="text-5xl sm:text-6xl font-semibold tracking-tighter tabular-numbers leading-none">2 999<span className="text-white/45 text-2xl ml-2">Kč</span></p>
                </div>
                <p className="text-white/45 line-through text-xl mb-4 tabular-numbers">6 500 Kč</p>
                <p className="label text-brand">Ušetříte 3 501 Kč · 54 % sleva</p>
                <a href="#form" className="mt-6 inline-flex items-center gap-2 bg-brand text-black px-6 py-3.5 label hover:bg-brand-dark transition-all w-full justify-center" data-testid="incl-cta">
                  Rezervovat termín
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </div>

          {/* 4 categories */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {INCLUDED.map((cat, i) => {
              const { num, title, subtitle, items } = cat;
              return (
                <motion.div key={num} {...fadeUp(i * 0.08)}
                  data-testid={`incl-${num}`}
                  className="bg-[#fafafa] hover:bg-black hover:text-white group p-5 sm:p-9 transition-colors duration-500 flex flex-col">
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="label text-brand">{num}</span>
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-semibold tracking-tighter mb-1">{title}</h3>
                  <p className={`label mb-7 text-black/45 group-hover:text-white/55 transition-colors`}>{subtitle}</p>
                  <ul className="space-y-3 flex-1">
                    {items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2.5">
                        <Check className="w-3.5 h-3.5 text-brand flex-shrink-0 mt-1" />
                        <span className={`text-sm leading-[1.6] text-black/70 group-hover:text-white/80 transition-colors`}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          <motion.p {...fadeUp(0.3)} className="text-center text-black/45 text-xs mt-10 max-w-xl mx-auto leading-relaxed">
            Konečnou nabídku potvrdíme při rezervaci termínu.
          </motion.p>

          {/* ═══ Price adjustments by vehicle size ═══ */}
          <motion.div {...fadeUp(0.35)} className="mt-16 sm:mt-20">
            <div className="max-w-3xl mx-auto">
              <p className="label text-brand text-center mb-6">Příplatky podle velikosti vozu</p>
              <div className="grid sm:grid-cols-3 gap-2">
                <div className="bg-[#fafafa] p-6 sm:p-7 text-center">
                  <p className="label text-brand mb-3">Základní cena</p>
                  <p className="text-sm text-black/70 mb-4">Sedan · Hatchback · Kombi</p>
                  <p className="text-3xl font-semibold tracking-tight text-black tabular-numbers">2 999 <span className="text-black/45 text-base">Kč</span></p>
                </div>
                <div className="bg-[#fafafa] p-6 sm:p-7 text-center">
                  <p className="label text-black/45 mb-3">+ 20 %</p>
                  <p className="text-sm text-black/70 mb-4">SUV · Crossover</p>
                  <p className="text-3xl font-semibold tracking-tight text-black tabular-numbers">3 599 <span className="text-black/45 text-base">Kč</span></p>
                </div>
                <div className="bg-[#fafafa] p-6 sm:p-7 text-center">
                  <p className="label text-black/45 mb-3">+ 30 %</p>
                  <p className="text-sm text-black/70 mb-4">Dodávka · MPV · 7 míst</p>
                  <p className="text-3xl font-semibold tracking-tight text-black tabular-numbers">3 899 <span className="text-black/45 text-base">Kč</span></p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 9. FAQ ═══ */}
      <section id="faq" className="py-20 sm:py-44 px-5 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-[1600px] mx-auto">
          <SectionEyebrow num="08" label="Časté otázky" />
          <div className="grid lg:grid-cols-12 gap-10">
            <motion.div {...fadeUp()} className="lg:col-span-5">
              <h2 className="text-4xl sm:text-7xl font-semibold tracking-[-0.04em] text-black mb-8 leading-[0.92]">
                Otázky,<br />které <span className="text-black/35">slyšíme.</span>
              </h2>
              <p className="text-black/55 text-base leading-relaxed max-w-md">
                Pokud nenajdete odpověď, zavolejte. Konzultace u nás je zdarma a nezávazná.
              </p>
            </motion.div>
            <motion.div {...fadeUp(0.1)} className="lg:col-span-7 border-t border-black/10">
              {FAQS.map((faq, i) => (
                <FAQItem key={i} q={faq.q} a={faq.a} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 10. FORM ═══ */}
      <section id="form" className="py-20 sm:py-44 px-5 sm:px-10 lg:px-16 bg-[#fafafa]">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <p className="label text-black/55 mb-7">— 09 · Rezervace</p>
            <h2 className="text-4xl sm:text-7xl lg:text-8xl font-semibold tracking-[-0.045em] text-black mb-7">
              Pojďme<br />začít.
            </h2>
            <p className="text-black/55 text-base sm:text-lg leading-[1.75] max-w-lg mx-auto">
              Vyplňte formulář. Seniorní detailer se vám ozve do 24 hodin s osobním plánem péče a nabídkou termínů.
            </p>
          </motion.div>

          <motion.form {...fadeUp(0.15)} onSubmit={handleSubmit} className="space-y-6 sm:space-y-7 bg-white p-6 sm:p-12 border border-black/8" data-testid="lead-form">
            {/* Branch */}
            <div>
              <label className="label text-black/55 mb-3 block">Pobočka *</label>
              <div className="grid grid-cols-2 gap-2">
                {BRANCHES.map((branch) => {
                  const { id, label, city } = branch;
                  const selected = formData.branch === id;
                  return (
                    <button key={id} type="button" onClick={() => setFormData((prev) => ({ ...prev, branch: id }))}
                      className={`p-4 text-left transition-all border ${selected ? 'bg-black text-white border-black' : 'bg-white text-black border-black/15 hover:border-black/40'}`}
                      data-testid={`branch-${id}`}>
                      <span className="text-base font-medium block">{city}</span>
                      <p className={`label mt-1 ${selected ? 'text-white/55' : 'text-black/45'}`}>{label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="label text-black/55 mb-3 block">Jméno *</label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Jan Novák"
                className="w-full bg-transparent border-b border-black/15 focus:border-black py-3.5 text-black text-base placeholder:text-black/30 focus:outline-none transition-all"
                data-testid="input-name" />
            </div>

            <div>
              <label className="label text-black/55 mb-3 block">Telefon *</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+420 123 456 789"
                className="w-full bg-transparent border-b border-black/15 focus:border-black py-3.5 text-black text-base placeholder:text-black/30 focus:outline-none transition-all"
                data-testid="input-phone" />
            </div>

            <div>
              <label className="label text-black/55 mb-3 block">Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="jan@email.cz"
                className="w-full bg-transparent border-b border-black/15 focus:border-black py-3.5 text-black text-base placeholder:text-black/30 focus:outline-none transition-all"
                data-testid="input-email" />
            </div>

            <div>
              <label className="label text-black/55 mb-3 block">Typ vozidla *</label>
              <div className="relative">
                <button type="button" onClick={() => setShowVehicleDropdown(!showVehicleDropdown)}
                  className="w-full bg-transparent border-b border-black/15 focus:border-black py-3.5 text-left flex items-center justify-between focus:outline-none transition-all"
                  data-testid="vehicle-dropdown">
                  <span className={formData.vehicle_type ? "text-black text-base" : "text-black/30 text-base"}>
                    {formData.vehicle_type ? VEHICLE_TYPES.find((v) => v.id === formData.vehicle_type)?.label : "Vyberte typ vozidla"}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-black/50 transition-transform ${showVehicleDropdown ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {showVehicleDropdown && (
                    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                      className="absolute top-full left-0 right-0 mt-1 bg-white border border-black/15 z-20 shadow-xl">
                      {VEHICLE_TYPES.map((v) => (
                        <button key={v.id} type="button" onClick={() => handleVehicleSelect(v.id)}
                          className={`w-full px-5 py-3.5 text-left text-sm hover:bg-black hover:text-white transition-colors ${formData.vehicle_type === v.id ? "text-black bg-black/[0.04]" : "text-black/75"}`}
                          data-testid={`vehicle-option-${v.id}`}>{v.label}</button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="border-t border-black/10 pt-7">
              <p className="label text-black/55 mb-3">Zvolený paket</p>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-black text-base font-medium">Kompletní detailing</p>
                  <p className="text-black/55 text-xs mt-0.5"><span className="text-brand font-semibold">2 999 Kč</span> · <span className="line-through">6 500 Kč</span></p>
                </div>
                <a href="#pricing" className="label text-black/55 hover:text-black transition-colors whitespace-nowrap">Detail →</a>
              </div>
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox checked={formData.gdpr_consent} onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, gdpr_consent: checked }))}
                className="mt-0.5 border-black/30 data-[state=checked]:bg-black data-[state=checked]:text-white data-[state=checked]:border-black" data-testid="gdpr-checkbox" />
              <label className="text-black/55 text-xs leading-relaxed cursor-pointer" onClick={() => setFormData((prev) => ({ ...prev, gdpr_consent: !prev.gdpr_consent }))}>
                Souhlasím se zpracováním osobních údajů za účelem zpracování poptávky dle GDPR.
              </label>
            </div>

            {error && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 border border-red-500/40 bg-red-50 text-red-600 text-sm" data-testid="error-message">{error}</motion.div>
            )}

            <button type="submit" disabled={isSubmitting} data-testid="submit-button"
              className="w-full bg-brand hover:bg-brand-dark disabled:bg-black/15 text-black disabled:text-white/40 py-5 label transition-all disabled:cursor-not-allowed group">
              {isSubmitting ? "Odesílám..." : (
                <span className="inline-flex items-center gap-3">
                  Odeslat poptávku
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1.5" />
                </span>
              )}
            </button>

            <div className="text-center pt-7 border-t border-black/10 mt-3">
              <p className="label text-black/45 mb-2">Preferujete osobní kontakt?</p>
              <a href="tel:+420730595595" className="text-black text-xl font-medium hover:opacity-60 transition-opacity" data-testid="phone-cta">+420 730 595 595</a>
            </div>
          </motion.form>
        </div>
      </section>

      {/* ═══ 11. FINAL CTA ═══ */}
      <section className="relative py-20 sm:py-52 px-5 sm:px-10 lg:px-16 bg-white border-t border-black/10">
        <div className="max-w-[1600px] mx-auto">
          <motion.div {...fadeUp()} className="text-center">
            <p className="label text-black/55 mb-10">— Závěr</p>
            <h2 className="text-4xl sm:text-7xl lg:text-[140px] lg:leading-[0.92] font-semibold tracking-[-0.045em] text-black mb-10 max-w-[1300px] mx-auto">
              Každý odraz <br />vypráví <span className="text-black/35">příběh.</span>
            </h2>
            <p className="text-black/55 text-base sm:text-lg max-w-lg mx-auto mb-12 leading-[1.75]">
              Jaký bude vyprávět vaše auto.
            </p>
            <a href="#form" data-testid="final-cta"
              className="group inline-flex items-center gap-3 bg-brand text-black px-10 py-5 label hover:bg-brand-dark transition-all">
              Rezervovat váš vůz
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1.5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ═══ Footer ═══ */}
      <footer className="bg-black text-white py-20 px-5 sm:px-10 lg:px-16">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid sm:grid-cols-12 gap-10 mb-16">
            <div className="sm:col-span-5">
              <p className="text-3xl sm:text-4xl font-bold tracking-tighter text-white">CARSPA<span className="text-brand">.</span></p>
              <p className="label text-white/45 mt-4">Premium Detailing Studio · Praha</p>
            </div>
            <div className="sm:col-span-4">
              <p className="label text-white mb-4">Čestlice</p>
              <p className="text-white/65 text-sm leading-relaxed">Aquapalace Praha<br />Pražská 138, 251 01</p>
              <a href="tel:+420730595595" className="text-white text-base mt-3 inline-block hover:opacity-70 transition-opacity font-medium">730 595 595</a>
            </div>
            <div className="sm:col-span-3">
              <p className="label text-white mb-4">Nové Butovice</p>
              <p className="text-white/65 text-sm leading-relaxed">Coral Office Park<br />Bucharova 8, Praha 5</p>
              <a href="tel:+420732327777" className="text-white text-base mt-3 inline-block hover:opacity-70 transition-opacity font-medium">732 327 777</a>
            </div>
          </div>
          <div className="hairline-soft mb-8" style={{ background: 'rgba(255,255,255,0.1)' }} />
          <div className="flex flex-wrap items-center justify-between gap-4 label text-white/45">
            <span>© 2026 CarSpa · Všechna práva vyhrazena</span>
            <span>Koch-Chemie · Rupes BigFoot · Swiss Wax</span>
          </div>
        </div>
      </footer>

      {/* ═══ Sticky Mobile CTA ═══ */}
      <div className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-white border-t border-black/10 p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <a href="#form" className="flex items-center justify-center gap-2 bg-brand text-black py-3.5 label" data-testid="sticky-cta">
          Rezervovat váš vůz
          <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}

// Helper component for inline spots in exclusivity section
function SpotsInline() {
  const [spots, setSpots] = useState(9);
  useEffect(() => {
    const fetchSpots = async () => {
      try { const r = await fetch(`${API}/api/spots`); const d = await r.json(); setSpots(d.spots); } catch (e) {}
    };
    fetchSpots();
    const interval = setInterval(fetchSpots, 30000);
    return () => clearInterval(interval);
  }, []);
  return <span className="text-white font-semibold">{String(spots).padStart(2, '0')}</span>;
}

function NavSpots() {
  const [spots, setSpots] = useState(9);
  useEffect(() => {
    const fetchSpots = async () => {
      try { const r = await fetch(`${API}/api/spots`); const d = await r.json(); setSpots(d.spots); } catch (e) {}
    };
    fetchSpots();
    const interval = setInterval(fetchSpots, 30000);
    return () => clearInterval(interval);
  }, []);
  return <span className="text-white font-semibold tabular-numbers">{String(spots).padStart(2, '0')}</span>;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
